import { environment } from '@uw-env/environment';
import { Injector } from '@angular/core';
import { Observable, throwError, map, catchError, timeout, retry, timer } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { ErrorModel } from '../models/error.model';
import { AppInjector } from '@uw/app.injector';
import { LoadingService } from '../interceptors/loader-spinner.service';

export abstract class BaseApiService {
  private readonly numberOfTries: number = 4; // Number of tries
  private readonly delayInRetry: number = 2000; // Number of miliseconds between each retry
  private getTimeout = 650000; // Total number of miliseconds to throw timeout error (for GET requests)
  private postTimeout = 650000; // Total number of miliseconds to throw timeout error (for POST requests)
  protected extendedTimeout = 780000; // Timeout for long-running operations
  private readonly skipRetryOnStatuses: Array<number> = [
    500, 402, 401, 400, 404, 403
  ];

  protected baseApiUrl: string;
  protected http: HttpClient;
  protected _router: Router;
  private _loadingSpinnerService: LoadingService;

  constructor(protected injector?: Injector) {
    const appInjector = AppInjector.getInjector() || injector;
    // Load dependencies using Injector so that we can decouple BaseApiService extending services
    this.http = appInjector.get(HttpClient);
    this._router = appInjector.get(Router);
    this.baseApiUrl = environment.apiURL;
    this._loadingSpinnerService = appInjector.get(LoadingService);

    this.getTimeout = environment.apiconfig.getTimeout
      ? environment.apiconfig.getTimeout
      : 650000;
    this.postTimeout = environment.apiconfig.postTimeout
      ? environment.apiconfig.postTimeout
      : 650000;
    this.extendedTimeout = environment.apiconfig.extendedTimeout
      ? environment.apiconfig.extendedTimeout
      : 780000;
  }

  // Get data from server
  httpGet(
    URL: string,
    requestOptions: any = this.getRequestOptions()
  ): Observable<any> {
    try {
      return this.http
        .get(URL, {  withCredentials: true })
        .pipe(
          retry({ count: this.numberOfTries - 1, delay: (error) => { if (this.skipRetryOnStatuses.indexOf(error.status) > -1) { throw error; } return timer(this.delayInRetry); } }),
          timeout(this.getTimeout),
          catchError(this.handleError.bind(this))
        );
    } catch (e) {
      return this.handleError(e);
    }
  }

  // Send data to server
  httpPost(
    URL: string,
    data: any,
    requestOptions: any = this.getRequestOptions(),
    errorHandling: boolean = true
  ): Observable<any> {
    try {
      if (errorHandling) {
        return this.http
          .post(URL, data, {  withCredentials: true })
          .pipe(timeout(this.postTimeout), catchError(this.handleError.bind(this)));
      } else {
        return this.http.post(URL, data).pipe(timeout(this.postTimeout));
      }
    } catch (e) {
      return this.handleError(e);
    }
  }

  // Get data from server using auth token
  httpAuthGet(
    URL: string,
    requestOptions: any = this.getRequestOptions()
  ): Observable<any> {
    try {
      // Check if user is logged in
      if (this.isTokenExpired()) {
        return throwError(() => 'You are not logged in!');
      }

      let options = Object.assign({}, requestOptions, {withCredentials: true});

      return this.http.get(URL, options).pipe(
        retry({ count: this.numberOfTries - 1, delay: (error) => { if (this.skipRetryOnStatuses.indexOf(error.status) > -1) { throw error; } return timer(this.delayInRetry); } }),
        timeout(this.getTimeout),
        catchError((error) => {
          error.url = URL;
          return this.handleError(error);
        })
      );
    } catch (e) {
      return this.handleError(e);
    }
  }

  httpAuthGetHtml(
    URL: string,
    requestOptions: any = this.getRequestHtmlResponseOption()
  ): Observable<any> {
    try {
      // Check if user is logged in
      if (this.isTokenExpired()) {
        return throwError(() => 'You are not logged in!');
      }

      return this.http.get(URL, {  withCredentials: true }).pipe(
        // retryWhen(this.retryStrategy()),
        // timeout(this.getTimeout),
        catchError(this.handleError.bind(this))
      );
    } catch (e) {
      return this.handleError(e);
    }
  }

  protected getRequestHtmlResponseOption(
    additionalHeaders?: HttpHeaders,
    responseType?: string
  ) {
    try {
      const options: any = {};
      let headers = new HttpHeaders(); // HttpHeaders is immutable - every set(), append(), delete() returns a new clone
      headers = headers.append('Accept', 'text/html');
      headers = headers.append('ResponseType', 'text');
      headers = headers.append('Content-Type', 'application/json');

      // Any additional headers
      if (additionalHeaders && additionalHeaders.keys().length) {
        additionalHeaders.keys().forEach((headerKey: string) => {
          if (
            headerKey.toLowerCase() === 'accept' ||
            headerKey.toLowerCase() === 'content-type'
          ) {
            // If headerKey is 'Accept' or 'Content-Type' delete previous one and use new one
            headers = headers.delete(headerKey);
          }
          headers = headers.append(
            headerKey,
            additionalHeaders.get(headerKey) || ''
          );
        });
      }

      if (responseType) {
        options['responseType'] = responseType;
      }

      options['headers'] = headers;
      options['responseType'] = 'text';

      return options;
    } catch (e) {
      return this.handleError(e);
    }
  }

  // Send data to server using auth token
  httpAuthPost(
    URL: string,
    data: any,
    requestOptions: any = this.getRequestOptions()
  ): Observable<any> {
    try {
      // Check if user is logged in
      if (this.isTokenExpired()) {
        return throwError(() => 'You are not logged in!');
      }

      return this.http
        .post(URL, data, {  withCredentials: true })
        .pipe(
          timeout(this.postTimeout),
          catchError(this.handleError.bind(this))
        );
    } catch (e) {
      return this.handleError(e);
    }
  }

  httpAuthPostDownload(
    URL: string,
    data: any,
    requestOptions: any = this.getRequestOptions(),
    timeoutMs?: number
  ): Observable<any> {
    try {
      return this.http
        .post(URL, data, {
          observe: 'response',
          responseType: 'blob',
         withCredentials: true,
        })
        .pipe(
          map((res) => {
            let filename = 'undefined';
            const disposition = res.headers.get('Content-Disposition');
            if (disposition) {
              const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = filenameRegex.exec(disposition);
              filename = matches
                ? matches[1].replace(/['"]/g, '')
                : 'undefined';
            }
            return {
              image: new Blob([res.body ? res.body : ''], {
                type: res.headers.get('Content-Type') ?? '',
              }),
              filename,
            };
          }),
          timeout(timeoutMs ?? this.postTimeout),
          catchError(this.handleError.bind(this))
        );
    } catch (e) {
      return this.handleError(e);
    }
  }

  httpAuthGetDownload(
    URL: string,
    requestOptions: any = this.getRequestOptions(),
    timeoutMs?: number
  ): Observable<any> {
    try {
      return this.http
        .get(URL, {
          observe: 'response',
          responseType: 'blob',
          withCredentials: true,
        })
        .pipe(
          map((res) => {
            let filename =  'undefined';
            // let filename =  this.getFilenameFromResponse(res)//'undefined';
            const disposition = res.headers.get('Content-Disposition');
            if (disposition) {
              const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = filenameRegex.exec(disposition);
              filename = matches
                ? matches[1].replace(/['"]/g, '')
                : 'undefined';
            }
            return {
              image: new Blob([res.body ? res.body : ''], {
                type: res.headers.get('Content-Type') ?? '',
              }),
              filename,
            };
          }),
          timeout(timeoutMs ?? this.getTimeout),
          catchError(this.handleError.bind(this))
        );
    } catch (e) {
      return this.handleError(e);
    }
  }

  getFilenameFromResponse(response: any): string {
    const contentDispositionHeader: string | null = response.headers.get('Content-Disposition');
    if (contentDispositionHeader) {
      const filenameMatch = contentDispositionHeader.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch.length > 1) {
        return filenameMatch[1];
      }
    }
    return ''; // Default filename if not found
  }

  // Send data to server using auth token
  httpAuthPut(
    URL: string,
    data: any,
    requestOptions: any = this.getRequestOptions()
  ): Observable<any> {
    try {
      // Check if user is logged in
      if (this.isTokenExpired()) {
        return throwError(() => 'You are not logged in!');
      }

      return this.http
        .put(URL, data, {  withCredentials: true })
        .pipe(
          timeout(this.postTimeout),
          catchError(this.handleError.bind(this))
        );
    } catch (e) {
      return this.handleError(e);
    }
  }

  httpAuthDelete(
    URL: string,
    requestOptions: any = this.getRequestOptions()
  ): Observable<any> {
    try {
      // Check if user is logged in
      if (this.isTokenExpired()) {
        return throwError(() => 'You are not logged in!');
      }

      return this.http
        .delete(URL, {  withCredentials: true })
        .pipe(catchError(this.handleError.bind(this)));
    } catch (e) {
      return this.handleError(e);
    }
  }

  // Usage:
  /*
    let requestHeaders = this.getRequestOptions( new HttpHeaders({'My-new-header': 'its-value'}) );
    responseType: 'arraybuffer' | 'blob' | 'json' | 'text'
  */
  protected getRequestOptions(
    additionalHeaders?: HttpHeaders,
    responseType?: string
  ) {
    try {
      const options: any = {};
      let headers = new HttpHeaders(); // HttpHeaders is immutable - every set(), append(), delete() returns a new clone
      headers = headers.append('Accept', 'application/json');
      headers = headers.append('Content-Type', 'application/json');

      // Any additional headers
      if (additionalHeaders && additionalHeaders.keys().length) {
        additionalHeaders.keys().forEach((headerKey: string) => {
          if (
            headerKey.toLowerCase() === 'accept' ||
            headerKey.toLowerCase() === 'content-type'
          ) {
            // If headerKey is 'Accept' or 'Content-Type' delete previous one and use new one
            headers = headers.delete(headerKey);
          }

          headers = headers.append(
            headerKey,
            additionalHeaders.get(headerKey) || ''
          );
        });
      }

      if (responseType) {
        options['responseType'] = responseType;
      }

      options['headers'] = headers;

      return options;
    } catch (e) {
      // console..log(`Exception`, e);
      return this.handleError(e);
    }
  }

  protected extractData(res: HttpResponse<any>) {
    const body = res.body;
    if (body) {
      return body.data != null ? body.data : body;
    }

    return null;
  }

  protected handleError(error: any): Observable<ErrorModel> {
    let errMsg = error.message
      ? error.message
      : error.status
      ? `${error.status} - ${error.statusText}`
      : 'Server error';
    let errCode = error.status ? error.status : null;

    // Sometimes handleError gets ErrorModel already so have to capture data from there
    if (error.code) {
      errCode = error.code;
    }

    let isExcludedLink = false;
    if (error.url) {
      isExcludedLink = error.url.endsWith('/login');
      // this._loadingSpinnerService.setLoading(false, error.url);
      this._loadingSpinnerService.setLoading(false);
    }

    if (errCode === 401 && !isExcludedLink) {
      // Logout user if server sends 401 for requests other than ending with /authenticate/login (for wrong email/pass)
      // this._router.navigate(['/auth/logout']);
      // this._router.navigate(['/error'], {
      //   queryParams: {
      //     erroCode: 'error.message_401',
      //   },
      // });

      // navigate user to appropriate login screen based on whether the user
      // is sso user or not.
      let isSSOUser = localStorage.getItem('isSSOUser');

      window.location.href = `${environment.donorSiteUrl}?authorizationFailed=true&isSSOUser=${isSSOUser}`;
      return throwError(() => ({
        code: errCode,
        message: 'Unauthorized User',
      }));
    }

    try {
      // If error is a valid JSON - pull out the responseStatus message (sent from server)
      const errorObj = error.error && error.error.error ? error.error.error :  error.error; // .json();	// Sometimes exists in response and sometime doesn't
      if (errorObj) {
        if (errorObj.message) {
          errMsg = errorObj.message;
        }

        if (typeof errorObj === 'string') {
          errMsg = errorObj;
        }

        if (errorObj.errorCode) {
          errCode = errorObj.errorCode;
        }
      }
    } catch (e) {
      // Cannot throw error here otherwise its shape changes
      // error.json was invalid
    }

    //in case of timeoput
    // if (error.message === 'Timeout has occurred'){
    //   this.loadingService.forceStopLoading(); <----- for remove loading bar
    //   this.showError('error.download_timeout');
    // } else {
    //   this.showError('error.file_download_error');
    // }

    return throwError(() => ({
      code: errCode,
      message: errMsg,
      details: error.error?.details ?? null,
    }));
  }

  isTokenExpired(): boolean {
    return false;
  }
}
